import { useEffect, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { toast } from "sonner"
import { Sparkles, MessageCircle, CheckCircle2, HelpCircle, Copy, Check, EyeOff, ThumbsUp, ThumbsDown } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, type SelectOption } from "@/components/ui/select"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { AppuserNotesPane } from "./appuser-notes-pane"
import { TicketStatusBadge } from "./ticket-status-badge"
import { TicketHistoryPanel } from "./ticket-history-panel"
import type { SharedProps } from "@/types/inertia"
import { ActivityFeedPane } from "./activity-feed-pane"
import { escalationReasonIcon } from "./escalation-reason-icon"
import { CustomerContextPane } from "../ui-document/customer-context-pane"
import { WorkflowGraphPanel } from "@/components/domain/workflow-graph"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { Ticket, TicketPriority, TicketCustomField, TicketSla, SlaState } from "@/types/ticket"

type MetaTab =
  | "details"
  | "customer_context"
  | "fields"
  | "notes"
  | "activity"
  | "workflow"
  | "history"
  | "actions"
  | "responses"

const PRIORITY_OPTIONS: SelectOption<TicketPriority>[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high",   label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low",    label: "Low" },
]

const UNASSIGNED_VALUE = "__unassigned__"

type Props = { ticket: Ticket }

// Right rail of the ticket-show 3-col layout. Surfaces requester,
// assignment, status / priority + closure actions. Two close paths:
//   - "Ask Feedback / Resolve" → posts a 👍/👎 prompt and soft-closes the
//     ticket as pending_feedback (auto-resolves in 1h if user doesn't rate)
//   - "Mark resolved" → hard close with no rating prompt
// Reassign is stubbed (no human_assignee column today).
export function TicketMetaPanel({ ticket }: Props) {
  const isClosed         = ticket.status === "closed"
  const isPendingFeedback = ticket.status === "pending_feedback"
  const isInactive       = isClosed || isPendingFeedback
  const [tab, setTab]    = useState<MetaTab>("details")
  const sharedProps      = usePage<SharedProps>().props
  const currentUserId    = sharedProps.current_user?.id ?? null
  const currentUserAdmin = sharedProps.isAdmin === true

  useEffect(() => {
    if (!currentUserAdmin && tab === "workflow") setTab("details")
  }, [currentUserAdmin, tab])

  // Closure actions (resolve / ask-feedback) live on <TicketActionFooter>
  // — see export below. They render in different positions per viewport
  // so the page hosts them, not this panel.

  const assigneeOptions: SelectOption<string>[] = [
    { value: UNASSIGNED_VALUE, label: "Unassigned" },
    ...(ticket.eligibleAssignees ?? []).map((u) => ({
      value: String(u.id),
      label: u.name ? `${u.name} (${u.email})` : u.email,
    })),
  ]
  const currentAssigneeValue = ticket.humanAssignee
    ? String(ticket.humanAssignee.id)
    : UNASSIGNED_VALUE
  const currentAssigneeOption =
    assigneeOptions.find((o) => o.value === currentAssigneeValue) ?? assigneeOptions[0]

  function reassign(value: string) {
    const userId = value === UNASSIGNED_VALUE ? "" : value
    router.post(
      `/tickets/${ticket.id}/reassign`,
      { user_id: userId },
      { preserveScroll: true },
    )
  }

  const currentPriorityOption =
    PRIORITY_OPTIONS.find((o) => o.value === ticket.priority) ?? PRIORITY_OPTIONS[2]

  function changePriority(value: TicketPriority) {
    if (value === ticket.priority) return
    router.patch(
      `/tickets/${ticket.id}`,
      { priority: value },
      { preserveScroll: true },
    )
  }

  const requesterInitial =
    (ticket.requester.name || ticket.requester.email || ticket.requester.sourceKey || "?")
      .trim()[0]
      ?.toUpperCase() ?? "?"
  const stateActive = ticket.requester.state === "active"

  const sourceStateField = (ticket.appuserCustomFields ?? []).find((x) => x.key === "source_state")

  return (
    // `lg:pb-28` reserves room at the bottom of the scroll region for
    // the desktop floating action bar (Ask feedback / Mark resolved),
    // which is absolutely-positioned inside the host aside in Show.tsx.
    // Without it, the last card (SLA summary's Resolution row) sits
    // under the action bar and the operator can't scroll past it. On
    // mobile the panel renders inside a <Sheet> with no overlaying
    // action bar (the sticky footer lives on the page, not inside the
    // sheet), so no extra padding is needed below `lg:`.
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:pb-28">
      {/* Top: Requester card — identity + escalation reason side-by-side.
          Left half: avatar + name/email, then User state + Source state as
          two labeled fields. Right half: red Lock icon + escalation reason
          headline + description, then Assigned AI below. The two closure
          actions live in a floating bar at the bottom of the panel. */}
      <Card variant="inset" className="p-5">
        {/* Requester identity + escalation reason side-by-side. Same
            reasoning as the cards below — the panel is 50% of viewport,
            so we only go side-by-side once the pane is wide enough
            (2xl: viewport ≥ 1536 px). Below that, requester sits
            above the escalation reason. */}
        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
          {/* Left half: requester identity + state fields. */}
          <div className="space-y-5 min-w-0">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brand-red/40 bg-brand-redSoft text-lg font-semibold text-brand-red">
                {requesterInitial}
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  href={`/appusers/${ticket.requester.appuserId}`}
                  className="block truncate text-lg font-semibold text-app-text hover:text-brand-red hover:underline"
                >
                  {ticket.requester.name || ticket.requester.sourceKey}
                </Link>
                {ticket.requester.email && (
                  <div className="truncate text-sm text-app-muted">{ticket.requester.email}</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <FieldLabel
                  label="Valet state"
                  hint="Valet's own account state for this user. 'Active' means they can converse with the AI; 'Banned' means a safety lockout has cut them off."
                />
                <div
                  className={cn(
                    "inline-flex items-center gap-2 text-sm",
                    stateActive ? "text-status-success" : "text-status-error",
                  )}
                >
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      stateActive ? "bg-status-success" : "bg-status-error",
                    )}
                  />
                  {capitalize(ticket.requester.state)}
                </div>
              </div>
              <div className="min-w-0">
                <FieldLabel
                  label="Source state"
                  hint="The partner's own classification of this user (e.g. verified, suspended, VIP). Synced from the partner's system; not editable here."
                />
                <SourceStateValue field={sourceStateField} />
              </div>
            </div>

            <div className="min-w-0">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">Source key</div>
              <CopyableSourceKey value={ticket.requester.sourceKey} />
            </div>
          </div>

          {/* Right half: escalation reason + assigned AI. The icon is
              chosen by Agents::ClassifyEscalationReason from a curated
              catalog (lock / credit_card / gift / etc.) and saved on the
              AgentEscalationReason row. Falls back to HelpCircle when no
              categorical reason is set yet. */}
          <div className="space-y-5 min-w-0">
            <EscalationReasonBlock ticket={ticket} />

            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">Assigned AI</div>
              <div className="flex items-center gap-2 text-base font-semibold text-app-text">
                <Sparkles size={16} strokeWidth={2} className="text-brand-red" />
                {ticket.assignedAgent ? ticket.assignedAgent.name : "Imported history"}
                <AiRatingIcon rating={ticket.aiUserRating} />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Below the requester card: tab strip spanning the full width.
          - Details:   current ticket state (status / priority / assignee / SLA).
          - Fields:    appuser + conversation custom fields (click-to-edit).
          - Activity:  this ticket's audit log (Activity rows). Inline here
                       — the tab IS the audit surface; valet-wide hide-
                       behind-icon rule doesn't apply.
          - History:   this requester's other tickets.
          - Actions:   placeholder for things we want to do with the user.
          - Responses: placeholder for canned-response shortcuts. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <div className="flex gap-1 border-b border-border">
          <TabButton active={tab === "details"}          onClick={() => setTab("details")}>Details</TabButton>
          <TabButton active={tab === "customer_context"} onClick={() => setTab("customer_context")}>
            Customer Context
            {(ticket.customerContextApps?.length ?? 0) > 0 && (
              <span className="ml-1 rounded-chip border border-border bg-app-panel2 px-1 text-[10px] font-medium text-app-muted">
                {ticket.customerContextApps?.length}
              </span>
            )}
          </TabButton>
          <TabButton active={tab === "fields"}   onClick={() => setTab("fields")}>Fields</TabButton>
          <TabButton active={tab === "notes"}    onClick={() => setTab("notes")}>Notes</TabButton>
          <TabButton active={tab === "activity"} onClick={() => setTab("activity")}>Activity</TabButton>
          {currentUserAdmin && (
            <TabButton active={tab === "workflow"} onClick={() => setTab("workflow")}>
              <span className="inline-flex items-center gap-1.5">
                Workflow
                <EyeOff size={13} strokeWidth={2} aria-label="Hidden from normal users" />
              </span>
            </TabButton>
          )}
          <TabButton active={tab === "history"}  onClick={() => setTab("history")}>History</TabButton>
          <TabButton active={tab === "actions"}  onClick={() => setTab("actions")}>Actions</TabButton>
          <TabButton active={tab === "responses"} onClick={() => setTab("responses")}>Responses</TabButton>
        </div>

        {tab === "customer_context" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {/* Lazy-loaded — the CustomerContextPane fetches each
                installed App's UI document on mount, and the mount only
                happens on the first time the operator clicks this tab.
                Multiple apps render stacked; one app failing doesn't
                break the others. */}
            <CustomerContextPane
              ticketId={ticket.id}
              apps={ticket.customerContextApps ?? []}
            />
          </div>
        )}

        {tab === "details" && (
          // Ticket overview + SLA summary cards. Stack until 2xl: —
          // the meta panel sits inside a 50%-of-viewport aside on
          // desktop, so a viewport of 1280 px (xl:) only gives the
          // pane ~640 px, which is too cramped for two cards. At
          // 2xl: (1536 px) the pane is ~768 px and cards have room
          // to breathe.
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto 2xl:grid-cols-2">
            <Card variant="inset" className="space-y-4 p-5">
              <h3 className="text-base font-semibold text-app-text">Ticket overview</h3>
              <Row label="Status">
                <TicketStatusBadge status={ticket.status} />
              </Row>
              <Row label="Priority">
                <div className="space-y-1">
                  <Select<TicketPriority>
                    value={currentPriorityOption}
                    options={PRIORITY_OPTIONS}
                    isDisabled={isInactive}
                    onChange={(o) => o && changePriority(o.value)}
                  />
                  {ticket.triageAppliedAt && (
                    <div className="text-[11px] text-app-subtle">
                      Auto-triaged {fmtCompactRelative(ticket.triageAppliedAt)}
                    </div>
                  )}
                </div>
              </Row>
              <Row label="Assignee">
                <Select<string>
                  value={currentAssigneeOption}
                  options={assigneeOptions}
                  isDisabled={isInactive}
                  onChange={(o) => o && reassign(o.value)}
                />
              </Row>
            </Card>

            <SlaSummaryCard sla={ticket.sla} />
          </div>
        )}

        {tab === "fields" && (
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
            <CustomFieldsCard
              ticketId={ticket.id}
              title="User fields"
              fields={ticket.appuserCustomFields ?? []}
            />
            <CustomFieldsCard
              ticketId={ticket.id}
              title="Conversation fields"
              fields={ticket.convoCustomFields ?? []}
            />
          </div>
        )}

        {tab === "notes" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {/* Operator-authored notes pinned to the appuser — distinct
                from the convo-scoped internal notes that intermix in the
                ticket thread. These travel WITH THE USER across every
                ticket they ever open. */}
            <AppuserNotesPane
              appuserId={ticket.requester.appuserId}
              notes={ticket.appuserNotes?.items ?? []}
              currentUserId={currentUserId}
              currentUserAdmin={currentUserAdmin}
              autoFocus
            />
          </div>
        )}

        {tab === "activity" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {/* Tickets are an exception to the valet-wide "hide activity
                behind Clock-icon modal" rule: this tab IS the audit surface,
                so the activity feed renders inline. */}
            <ActivityFeedPane entries={ticket.changeLog ?? []} compact />
          </div>
        )}

        {tab === "workflow" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <WorkflowGraphPanel
              trace={ticket.workflowTrace ?? null}
              messages={(ticket.messages ?? []).map((message) => ({
                id: message.id,
                role: message.role,
                content: message.content,
                turnNumber: message.turnNumber,
                createdAt: message.createdAt,
              }))}
              title="Conversation workflow"
              subtitle="Live workflow state for this ticket, including the active Skill Card, current step, requirements, and recent actions."
              height={720}
            />
          </div>
        )}

        {tab === "history" && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <TicketHistoryPanel rows={ticket.appuserHistory ?? []} />
          </div>
        )}

        {tab === "actions" && (
          <Card variant="inset" className="p-6 text-center text-sm text-app-subtle">
            No actions yet.
          </Card>
        )}

        {tab === "responses" && (
          <Card variant="inset" className="p-6 text-center text-sm text-app-subtle">
            Responses — coming soon.
          </Card>
        )}
      </div>
    </div>
  )
}

// Closure-action bar — extracted from <TicketMetaPanel> so the same two
// buttons can render in different host positions per viewport:
//   - desktop: floats inside the right meta-panel aside (absolute,
//     pointer-events-auto)
//   - mobile: sticky at the bottom of the page (lg:hidden), so the
//     buttons stay reachable without the operator having to open the
//     details Sheet to find them.
// Pages place this where they want; both close-paths still hit the same
// `/resolve` and `/ask_feedback` controller actions.
export function TicketActionFooter({
  ticket,
  className,
}: {
  ticket: Ticket
  className?: string
}) {
  const isClosed          = ticket.status === "closed"
  const isPendingFeedback = ticket.status === "pending_feedback"
  const isInactive        = isClosed || isPendingFeedback

  function resolve() {
    if (isInactive) return
    router.post(`/tickets/${ticket.id}/resolve`)
  }

  function askFeedback() {
    if (isInactive) return
    router.post(`/tickets/${ticket.id}/ask_feedback`)
  }

  return (
    <div className={cn("flex gap-3 rounded-card border border-border bg-app-panel2 p-3 shadow-2xl shadow-black/40", className)}>
      <Button
        type="button"
        variant="gold"
        className="flex-1 px-5 py-3 text-sm"
        onClick={askFeedback}
        disabled={isInactive}
      >
        <MessageCircle size={16} strokeWidth={2} className="mr-2" />
        {isPendingFeedback ? "Awaiting feedback" : "Ask for feedback & resolve"}
      </Button>
      <Button type="button" className="flex-1 px-5 py-3 text-sm" onClick={resolve} disabled={isInactive}>
        <CheckCircle2 size={16} strokeWidth={2} className="mr-2" />
        {isClosed ? "Already closed" : isPendingFeedback ? "Soft-closed" : "Mark resolved"}
      </Button>
    </div>
  )
}

// Renders the partner-supplied lifecycle marker as a static value. Reads
// the value from the appuser custom-fields payload (key: "source_state").
// Display-only — operators do not edit source_state from this surface
// (it's owned by the partner via the s2s API). For select-typed CFDs we
// honor the operator-configured choice `color` so the chip mirrors the
// Valet state pill visually (e.g. green Active, red Banned).
function SourceStateValue({ field }: { field: TicketCustomField | undefined }) {
  const isUnset = !field || field.value === null || field.value === undefined || field.value === ""
  let display = "—"
  let color: string | undefined

  if (!isUnset && field) {
    if (Array.isArray(field.value)) display = field.value.join(", ")
    else if (typeof field.value === "boolean") display = field.value ? "Yes" : "No"
    else if (field.fieldType === "select" && typeof field.value === "string") {
      const choices = Array.isArray(field.options.choices) ? field.options.choices : []
      const found = choices.find((c) => (typeof c === "string" ? c === field.value : c.value === field.value))
      if (!found) {
        display = String(field.value)
      } else if (typeof found === "string") {
        display = found
      } else {
        display = found.label
        color = found.color
      }
    } else display = String(field.value)
  }

  const tone = SOURCE_STATE_COLOR_TONES[color ?? ""] ?? null
  if (isUnset || !tone) {
    return (
      <div
        className={cn("text-sm", isUnset ? "text-app-subtle" : "text-app-text")}
        title={field?.description ?? "Source state from partner (custom field key: source_state)"}
      >
        {display}
      </div>
    )
  }
  return (
    <div
      className={cn("inline-flex items-center gap-2 text-sm", tone.text)}
      title={field?.description ?? "Source state from partner (custom field key: source_state)"}
    >
      <span className={cn("h-2 w-2 rounded-full", tone.dot)} />
      {display}
    </div>
  )
}

// Maps the operator-chosen choice `color` (free-form string keyed off the
// CFD options.choices[].color) to Valet status tokens. Anything outside
// this set falls back to the plain text rendering — never let an
// arbitrary partner color break the panel's color discipline.
const SOURCE_STATE_COLOR_TONES: Record<string, { dot: string; text: string }> = {
  green:  { dot: "bg-status-success", text: "text-status-success" },
  red:    { dot: "bg-status-error",   text: "text-status-error" },
  yellow: { dot: "bg-status-warning", text: "text-status-warning" },
  amber:  { dot: "bg-status-warning", text: "text-status-warning" },
  blue:   { dot: "bg-brand-red",      text: "text-app-text" },
  gray:   { dot: "bg-app-muted",      text: "text-app-muted" },
  grey:   { dot: "bg-app-muted",      text: "text-app-muted" },
}

function capitalize(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Header card slot showing the icon + reason name + clamped description.
// On hover (or keyboard focus), pops a popover beneath the block with
// the un-clamped description AND the LLM's verbatim free-text reason —
// so operators can audit "why did the AI decide this category" without
// leaving the ticket page.
function EscalationReasonBlock({ ticket }: { ticket: Ticket }) {
  const [open, setOpen] = useState(false)
  const reason = ticket.escalationReason
  const Icon = reason ? escalationReasonIcon(reason.icon) : HelpCircle
  const hasPopoverContent = !!(reason?.description || ticket.escalationReasonText)

  return (
    <div
      className="relative"
      onMouseEnter={() => hasPopoverContent && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={(e) => { if (hasPopoverContent && e.target.matches(":focus-visible")) setOpen(true) }}
      onBlur={() => setOpen(false)}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brand-red/40 bg-brand-redSoft text-brand-red">
          <Icon size={22} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-app-subtle">Escalation reason</div>
          {reason ? (
            <>
              <div
                className={cn(
                  "truncate text-base font-semibold text-app-text",
                  reason.status === "pending" ? "opacity-70" : "",
                  hasPopoverContent ? "cursor-help" : "",
                )}
                tabIndex={hasPopoverContent ? 0 : -1}
              >
                {reason.name}
                {reason.status === "pending" && (
                  <span className="ml-1 text-[11px] uppercase tracking-wide text-app-subtle">(pending)</span>
                )}
              </div>
              {(reason.description || ticket.escalationReasonText) && (
                <p className="mt-0.5 line-clamp-2 text-sm text-app-muted">
                  {reason.description || ticket.escalationReasonText}
                </p>
              )}
            </>
          ) : ticket.escalationReasonText ? (
            <p className="mt-0.5 text-sm text-app-muted whitespace-pre-wrap">{ticket.escalationReasonText}</p>
          ) : (
            <p className="mt-0.5 text-sm text-app-subtle">—</p>
          )}
        </div>
      </div>

      {open && hasPopoverContent && (
        <div
          role="tooltip"
          className="absolute left-0 right-0 top-full z-30 mt-2 space-y-3 rounded-card border border-border bg-app-panel2 p-4 text-sm leading-relaxed text-app-text shadow-2xl shadow-black/50"
        >
          {reason && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">Category</div>
              <div className="text-base font-semibold text-app-text">{reason.name}</div>
              {reason.description && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-app-muted">{reason.description}</p>
              )}
            </div>
          )}
          {ticket.escalationReasonText && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-app-subtle">
                {reason ? "Verbatim reason from agent" : "Reason from agent"}
              </div>
              <p className="whitespace-pre-wrap text-sm text-app-text">{ticket.escalationReasonText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AiRatingIcon({ rating }: { rating: Ticket["aiUserRating"] }) {
  if (rating === "good") {
    return (
      <ThumbsUp
        size={16}
        strokeWidth={2}
        className="text-status-success"
        aria-label="User rated this AI conversation good"
      />
    )
  }
  if (rating === "bad") {
    return (
      <ThumbsDown
        size={16}
        strokeWidth={2}
        className="text-status-error"
        aria-label="User rated this AI conversation needs improvement"
      />
    )
  }
  return null
}

// Source-key chip: clicking the row (or the icon) copies the partner-side
// id to the clipboard. Same one-shot navigator.clipboard pattern used by
// api-key-field.tsx — no library needed.
function CopyableSourceKey({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable (insecure context, sandboxed iframe). Silent.
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : "Click to copy"}
      className={cn(
        "group flex w-full items-center gap-2 truncate rounded-control border bg-black/30 px-2 py-1 font-mono text-[11px] text-app-text transition",
        copied
          ? "border-status-success/50 text-status-success"
          : "border-border hover:border-brand-red/50 hover:bg-black/40",
      )}
    >
      <span className="flex-1 truncate text-left">{value}</span>
      {copied ? (
        <Check size={12} strokeWidth={2.5} className="shrink-0" />
      ) : (
        <Copy size={12} strokeWidth={2} className="shrink-0 text-app-muted group-hover:text-app-text" />
      )}
    </button>
  )
}

// Small uppercase label with an inline hint. Uses the shared InfoTooltip
// (hover-or-focus popover) instead of the native `title` attribute so the
// hint is actually visible without waiting for the OS hover delay.
function FieldLabel({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wide text-app-subtle">
      <span>{label}</span>
      <InfoTooltip side="bottom">{hint}</InfoTooltip>
    </div>
  )
}

// Label-left / value-right row used inside the Ticket overview card.
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-sm text-app-muted">{label}</div>
      <div className="min-w-0 flex-1 max-w-[60%]">{children}</div>
    </div>
  )
}

// Compact SLA summary used inside the Details tab. One row per clock,
// each with a tinted bar that visualizes met/breached/near-breach state
// and a short status label on the right.
function SlaSummaryCard({ sla }: { sla: TicketSla }) {
  return (
    <Card variant="inset" className="space-y-4 p-5">
      <h3 className="text-base font-semibold text-app-text">SLA summary</h3>
      <SlaSummaryRow label="First response" state={sla.firstResponse.state} dueAt={sla.firstResponse.dueAt} metAt={sla.firstResponse.metAt} breachedAt={sla.firstResponse.breachedAt} />
      <SlaSummaryRow label="Idle response" state={sla.idleResponse.state} dueAt={sla.idleResponse.dueAt} breachedAt={sla.idleResponse.firstBreachedAt} />
      <SlaSummaryRow label="Resolution"    state={sla.resolution.state}    dueAt={sla.resolution.dueAt}    breachedAt={sla.resolution.breachedAt} />
    </Card>
  )
}

function SlaSummaryRow({ label, state, dueAt, metAt, breachedAt }: {
  label: string
  state: SlaState
  dueAt: string | null
  metAt?: string | null
  breachedAt?: string | null
}) {
  // Status label + bar tint mirror the SlaPill semantics — green for met,
  // red for breached, amber for near_breach, neutral for not_yet.
  let status = ""
  let tone: "ok" | "breach" | "warn" | "muted" = "muted"
  if (state === "met") {
    status = "MET"
    tone = "ok"
  } else if (state === "breached") {
    const ref = breachedAt || dueAt
    status = ref ? `OVERDUE ${fmtCompactRelative(ref).replace(/^[+-]?/, "")}` : "OVERDUE"
    tone = "breach"
  } else if (state === "near_breach") {
    status = dueAt ? `NEAR ${fmtCompactRelative(dueAt)}` : "NEAR BREACH"
    tone = "warn"
  } else if (state === "not_yet") {
    status = dueAt ? `DUE ${fmtCompactRelative(dueAt)}` : "ON TRACK"
    tone = "muted"
  } else {
    status = "—"
    tone = "muted"
  }
  void metAt  // Kept in the prop list for parity with SlaSummaryCard wiring.
  const barCls =
    tone === "ok"     ? "bg-status-success" :
    tone === "breach" ? "bg-status-error" :
    tone === "warn"   ? "bg-status-warning" :
                        "bg-app-panel2"
  const textCls =
    tone === "ok"     ? "text-status-success" :
    tone === "breach" ? "text-status-error" :
    tone === "warn"   ? "text-status-warning" :
                        "text-app-muted"
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-app-muted">{label}</div>
        <div className={cn("text-xs font-semibold uppercase tracking-wide", textCls)}>{status}</div>
      </div>
      <div className={cn("h-1 w-full rounded-full", barCls)} />
    </div>
  )
}

// Operator-facing custom-fields card. Renders every `live` definition for
// the parent_type, with the value (or em-dash for unset). PII-flagged
// values come down pre-redacted as "<redacted>" from the BE and are
// display-only.
function CustomFieldsCard({ ticketId, title, fields }: {
  ticketId: string
  title: string
  fields: TicketCustomField[]
}) {
  return (
    <Card variant="inset" className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-app-text">{title}</h3>
        <span className="text-[11px] text-app-muted">{fields.length}</span>
      </div>
      {fields.length === 0 ? (
        <p className="text-sm text-app-subtle">No fields defined.</p>
      ) : (
        <dl className="space-y-4">
          {fields.map((f) => (
            <CustomFieldRow key={f.key} ticketId={ticketId} field={f} />
          ))}
        </dl>
      )}
    </Card>
  )
}

// Single row, click-to-edit. Shows label + current value; clicking the
// value swaps the cell into the type-appropriate input. Enter/blur saves;
// Escape cancels. Saves go through PATCH /tickets/:id/custom_field which
// routes through Companies::CustomFields::UpsertValues with source: :portal.
function CustomFieldRow({ ticketId, field }: { ticketId: string; field: TicketCustomField }) {
  const [editing, setEditing] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  // Optimistic value held during the gap between fetch-success and the
  // Inertia partial reload finishing. Without this, the display flickers:
  // editor closes → row re-renders with stale `field.value` → reload
  // completes → row re-renders with new value. With this, the display
  // skips the stale frame.
  const [optimistic, setOptimistic] = useState<TicketCustomField["value"] | undefined>(undefined)

  useEffect(() => {
    if (optimistic !== undefined &&
        JSON.stringify(field.value ?? null) === JSON.stringify(optimistic ?? null)) {
      setOptimistic(undefined)
    }
  }, [field.value, optimistic])

  const canEdit = field.editable
  const displayedField: TicketCustomField =
    optimistic === undefined ? field : { ...field, value: optimistic }

  function commit(nextValue: TicketCustomField["value"], { unset = false } = {}) {
    // No-op if nothing changed (avoid noise on simple click-away).
    if (!unset && JSON.stringify(nextValue ?? null) === JSON.stringify(field.value ?? null)) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError(null)
    const targetValue: TicketCustomField["value"] = unset ? null : nextValue
    setOptimistic(targetValue)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    fetch(`/tickets/${ticketId}/custom_field`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrf },
      body: JSON.stringify({ scope: field.parentType, key: field.key, value: nextValue, unset }),
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}))
        if (!r.ok || body?.ok === false) {
          const msg = body?.error || `Save failed (${r.status}).`
          setError(msg)
          toast.error(`${field.label}: ${msg}`)
          setOptimistic(undefined)  // roll back the optimistic update
          return
        }
        toast.success(`${field.label} updated.`)
        router.reload({ only: ["ticket"] })
        setEditing(false)
      })
      .catch((e) => {
        const msg = e?.message || "Save failed."
        setError(msg)
        toast.error(`${field.label}: ${msg}`)
        setOptimistic(undefined)
      })
      .finally(() => setSaving(false))
  }

  function cancel() {
    setEditing(false)
    setError(null)
  }

  return (
    <div className="min-w-0">
      <dt
        className="text-xs text-app-subtle"
        title={field.description ?? field.key}
      >
        {field.label}
      </dt>
      <dd className="mt-1 min-w-0 text-sm text-app-text">
        {editing && canEdit ? (
          <CustomFieldEditor field={field} onCommit={commit} onCancel={cancel} saving={saving} />
        ) : (
          <button
            type="button"
            disabled={!canEdit}
            onClick={() => canEdit && setEditing(true)}
            className={cn(
              "block w-full min-w-0 rounded-control py-0.5 text-left leading-snug",
              canEdit ? "cursor-text hover:bg-white/5" : "cursor-not-allowed",
            )}
            title={canEdit ? "Click to edit" : "Read-only"}
          >
            {formatCustomFieldValue(displayedField)}
          </button>
        )}
        {error && <div className="mt-1 text-[11px] text-status-error">{error}</div>}
      </dd>
    </div>
  )
}

function CustomFieldEditor({
  field, onCommit, onCancel, saving,
}: {
  field: TicketCustomField
  onCommit: (next: TicketCustomField["value"], opts?: { unset?: boolean }) => void
  onCancel: () => void
  saving: boolean
}) {
  const [draft, setDraft]         = useState<TicketCustomField["value"]>(field.value)
  // ESC sets this so the queued onBlur doesn't double-save the cancelled value.
  const [cancelled, setCancelled] = useState(false)

  function commitDraft(value: TicketCustomField["value"]) {
    if (cancelled) return
    const isEmpty = value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)
    onCommit(isEmpty ? null : value, { unset: isEmpty })
  }

  function handleEscape(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); setCancelled(true); onCancel() }
  }

  function handleEnterAndEscape(e: React.KeyboardEvent<HTMLInputElement>) {
    handleEscape(e)
    if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur() }
  }

  // Choices are stored as [{value, label}] in the def's `options.choices`
  // jsonb. Defensive: tolerate the legacy string-array shape too.
  const rawChoices = Array.isArray(field.options.choices) ? field.options.choices : []
  const choices: Array<{ value: string; label: string }> = rawChoices.map((c) =>
    typeof c === "string" ? { value: c, label: c } : { value: String(c.value), label: String(c.label ?? c.value) }
  )
  const inputCls = "min-w-0 flex-1 rounded-control border border-border bg-app-panel2 px-2 py-1 text-sm text-app-text"

  switch (field.fieldType) {
    case "boolean":
      return (
        <select
          autoFocus
          disabled={saving}
          value={draft === true ? "true" : draft === false ? "false" : ""}
          onChange={(e) => {
            const next = e.target.value === "" ? null : e.target.value === "true"
            setDraft(next); commitDraft(next)
          }}
          onKeyDown={handleEscape}
          className={inputCls}
        >
          <option value="">—</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      )
    case "select":
      return (
        <select
          autoFocus
          disabled={saving}
          value={typeof draft === "string" ? draft : ""}
          onChange={(e) => {
            const next = e.target.value === "" ? null : e.target.value
            setDraft(next); commitDraft(next)
          }}
          onKeyDown={handleEscape}
          className={inputCls}
        >
          <option value="">—</option>
          {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      )
    case "multi_select": {
      // Each toggle is a deliberate action — commit immediately so the user
      // doesn't have to manage a draft. ESC exits edit mode without further
      // saves; the last toggle is already on the server.
      const selected = Array.isArray(draft) ? draft : []
      return (
        <div className="flex flex-wrap gap-1" onKeyDown={handleEscape} tabIndex={-1}>
          {choices.map((c) => {
            const on = selected.includes(c.value)
            return (
              <button
                type="button"
                key={c.value}
                disabled={saving}
                onClick={() => {
                  const next = on ? selected.filter((x) => x !== c.value) : [...selected, c.value]
                  setDraft(next); commitDraft(next)
                }}
                className={cn(
                  "rounded-chip border px-1.5 py-0.5 text-[11px]",
                  on
                    ? "border-brand-red bg-brand-redSoft text-brand-red"
                    : "border-border bg-app-panel2 text-app-muted hover:text-app-text",
                )}
              >
                {c.label}
              </button>
            )
          })}
        </div>
      )
    }
    case "number":
      return (
        <input
          autoFocus
          type="number"
          disabled={saving}
          value={draft === null || draft === undefined ? "" : String(draft)}
          onChange={(e) => setDraft(e.target.value === "" ? null : Number(e.target.value))}
          onBlur={() => commitDraft(draft)}
          onKeyDown={handleEnterAndEscape}
          className={inputCls}
        />
      )
    case "date":
      return (
        <input
          autoFocus
          type="date"
          disabled={saving}
          value={typeof draft === "string" ? draft : ""}
          onChange={(e) => setDraft(e.target.value === "" ? null : e.target.value)}
          onBlur={() => commitDraft(draft)}
          onKeyDown={handleEnterAndEscape}
          className={inputCls}
        />
      )
    default: // "text"
      return (
        <input
          autoFocus
          type="text"
          disabled={saving}
          value={typeof draft === "string" ? draft : ""}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitDraft(draft)}
          onKeyDown={handleEnterAndEscape}
          className={inputCls}
        />
      )
  }
}

function formatCustomFieldValue(f: TicketCustomField) {
  if (f.value === null || f.value === undefined || f.value === "") {
    return <span className="text-app-subtle">—</span>
  }
  // For select / multi_select, the stored value is the choice's `value`
  // but the display should be its `label`. Defensive: tolerate the legacy
  // string-array shape (label === value).
  const labelFor = (v: string): string => {
    const choices = Array.isArray(f.options.choices) ? f.options.choices : []
    const found = choices.find((c) => (typeof c === "string" ? c === v : c.value === v))
    if (!found) return v
    return typeof found === "string" ? found : found.label
  }

  if (Array.isArray(f.value)) {
    return f.value.length === 0 ? <span className="text-app-subtle">—</span> : (
      <span className="flex flex-wrap gap-1">
        {f.value.map((v, i) => (
          <span key={`${v}-${i}`} className="rounded-chip border border-border bg-app-panel2 px-1.5 py-0.5 text-[11px] text-app-text">
            {f.fieldType === "multi_select" ? labelFor(String(v)) : String(v)}
          </span>
        ))}
      </span>
    )
  }
  if (typeof f.value === "boolean") return f.value ? "Yes" : "No"
  if (f.fieldType === "select" && typeof f.value === "string") {
    return <span className="break-words">{labelFor(f.value)}</span>
  }
  return <span className="break-words">{String(f.value)}</span>
}

function TabButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-3 py-2 text-xs font-medium uppercase tracking-wide transition",
        active
          ? "border-brand-red text-app-text"
          : "border-transparent text-app-muted hover:text-app-text",
      )}
    >
      {children}
    </button>
  )
}
